import styles from "./styles.module.scss";
import {
  deliveryData,
  months,
  types,
  TypesColors,
  typesMarksMap,
} from "./mock";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import { generateLinksFromDeliveryData } from "./utils";

export const GanttChart = () => {
  const { chartData, yAxisData } = useMemo(() => {
    type Period = {
      start: string;
      end: string;
      type?: keyof typeof typesMarksMap;
    };

    const chartData: Array<{
      name: string;
      value: [number, number, number, boolean, string, string, number];
      itemStyle: { color: string };
    }> = [];
    const yAxisData: string[] = [];
    let rowIndex = 0;

    const addPeriodToChart = (
      asin: string,
      rowType: string,
      key: number,
      period: Period,
      isPredict: boolean,
      idx: number,
    ) => {
      const startDate = new Date(period.start).getTime();
      const endDate = new Date(period.end).getTime();
      const widthInDays = (endDate - startDate) / (1000 * 60 * 60 * 24);

      const icon = period.type ? typesMarksMap[period.type]?.icon || "" : "";
      const color = TypesColors[rowType.toUpperCase()] || "#ccc";

      chartData.push({
        name: `${asin} - ${key}`,
        value: [idx, startDate, endDate, isPredict, icon, color, widthInDays],
        itemStyle: {
          color: "transparent",
        },
      });
    };

    deliveryData.forEach((item) => {
      const asin = item.id;

      item.keys.forEach((row, keyIdx) => {
        yAxisData.push(keyIdx === 0 ? asin : "");

        const pastedPeriods = row.periods.pasted as Period[];
        const predictPeriods = row.periods.predict as Period[];
        const allPeriods = [...pastedPeriods, ...predictPeriods];

        allPeriods.forEach((period) => {
          const isPredict = predictPeriods.includes(period);
          addPeriodToChart(
            asin,
            row.type,
            row.key,
            period,
            isPredict,
            rowIndex,
          );
        });

        rowIndex += 1;
      });
    });

    return { chartData, yAxisData };
  }, []);

  const links = useMemo(() => generateLinksFromDeliveryData(deliveryData), []);

  const option = useMemo(() => {
    return {
      grid: {
        top: 60,
        left: 100,
        right: 16,
        bottom: 16,
      },
      tooltip: { show: false },
      xAxis: {
        type: "time" as const,
        position: "top" as const,
        min: new Date("2024-01-01").getTime(),
        max: new Date("2024-12-31").getTime(),
        axisLabel: {
          formatter: (value: number) => {
            const date = new Date(value);
            return months[date.getMonth()];
          },
          color: "#888",
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: "#333",
            type: "solid" as const,
          },
        },
      },
      yAxis: {
        type: "category" as const,
        data: yAxisData,
        inverse: true,
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: {
          show: true,
          lineStyle: {
            color: "#333",
            type: "solid" as const,
          },
        },
      },
      series: [
        {
          type: "custom" as const,
          renderItem: (api: any) => {
            const categoryIndex = api.value(0);
            const start = api.coord([api.value(1), categoryIndex]);
            const end = api.coord([api.value(2), categoryIndex]);
            const dashed = api.value(3);
            const icon = api.value(4);
            const color = api.value(5);
            const width = end[0] - start[0];
            const height = 30;

            return {
              type: "group",
              children: [
                {
                  type: "rect",
                  shape: {
                    x: start[0],
                    y: start[1] - height / 2,
                    width,
                    height,
                    r: [4, 4, 4, 4],
                  },
                  style: {
                    fill: dashed ? color + "80" : color,
                    stroke: dashed ? color : undefined,
                    lineDash: dashed ? [4, 3] : undefined,
                    lineWidth: dashed ? 1 : 0,
                  },
                },
                ...(icon
                  ? [
                      {
                        type: "text",
                        style: {
                          text: icon,
                          x: start[0] + width / 2,
                          y: start[1],
                          textAlign: "center" as const,
                          textVerticalAlign: "middle" as const,
                          fontSize: 12,
                        },
                      },
                    ]
                  : []),
              ],
            };
          },
          encode: {
            x: [1, 2],
            y: 0,
          },
          data: chartData,
        },
        {
          type: "lines" as const,
          coordinateSystem: "cartesian2d" as const,
          z: 100,
          effect: {
            show: true,
            symbol: "arrow",
            symbolSize: 6,
            trailLength: 0,
            period: 4,
            loop: true,
            symbolKeep: true,
          },
          lineStyle: {
            color: "#aaa",
            width: 1,
            type: "solid" as const,
            curveness: 0.3,
          },
          data: links.map((link) => ({
            coords: [
              [link.from, link.fromIdx],
              [link.to, link.toIdx],
            ],
          })),
        },
      ],
    };
  }, [chartData, yAxisData]);

  return (
    <div className={styles.deliveryWrapper}>
      <div className={styles.typeList}>
        {types.map((type) => (
          <LegendItem key={type.label} type={type} />
        ))}
      </div>
      <div className={styles.chartWrapper}>
        <ReactECharts
          option={option}
          style={{ height: `${yAxisData.length * 60}px`, width: "100%" }}
          notMerge
          lazyUpdate
        />
      </div>
    </div>
  );
};

const LegendItem = ({
  type,
}: {
  type: { label: string; color: string; icon: string | null };
}) => {
  return (
    <li className={styles.typeItem}>
      <span
        className={styles.legendIcon}
        style={{
          backgroundColor: type.color,
        }}
      >
        {type.icon ? (
          <span
            className={styles.svgIcon}
            dangerouslySetInnerHTML={{ __html: type.icon }}
          />
        ) : (
          <span
            className={styles.dot}
            style={{ backgroundColor: type.color }}
          />
        )}
      </span>
      <span className={styles.legendLabel}>{type.label}</span>
    </li>
  );
};
